import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import useServer from '@/api/useServerMain'
import { toast } from 'react-toastify'
import ButtonLoading from '../misc/Button'

const fileTypeMappings: Record<string, string[]> = {
  images: ['image/jpeg', 'image/png'],
  pdf: ['application/pdf'],
  word: [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  excel: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  txt: ['text/plain'],
}
const typeToTextMapping: Record<string, string> = {
  images: 'Imágenes (.jpeg, .png)',
  pdf: 'PDF',
  word: 'Word (DOC, DOCX)',
  excel: 'Excel (XLS, XLSX)',
  txt: 'Texto (TXT)',
}

type AcceptTypes = 'images' | 'pdf' | 'word' | 'excel' | 'txt' | 'all'

interface UploadFileProps {
  onUpload: (file: File) => void
  acceptedFileTypes: AcceptTypes[]
  loading: boolean
  onClear?: () => void
  clearTrigger?: boolean
}

export default function UploadFile({
  onUpload,
  acceptedFileTypes,
  loading,
  onClear,
  clearTrigger,
}: UploadFileProps) {
  const [dragActive, setDragActive] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  // Clear file when clearTrigger changes
  useEffect(() => {
    if (clearTrigger) {
      clearFilePreview()
      setFile(null)
      onClear?.()
    }
  }, [clearTrigger, onClear])

  if (acceptedFileTypes.includes('all')) {
    acceptedFileTypes = Object.keys(fileTypeMappings) as AcceptTypes[]
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(true)
  }

  const handleDragLeave = () => {
    setDragActive(false)
  }
  const getAcceptedFileTypesText = () => {
    return acceptedFileTypes
      .map((type) => typeToTextMapping[type] || type)
      .join(', ')
  }

  const createFilePreview = (file: File) => {
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
    } else {
      setPreviewUrl(null)
    }
  }

  const clearFilePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0]
      const acceptedTypes = getAcceptedFileTypesForInput().split(',')
      if (acceptedTypes.includes(droppedFile.type) && validateImageFile(droppedFile)) {
        clearFilePreview()
        setFile(droppedFile)
        createFilePreview(droppedFile)
      } else {
        let errorMessage = 'Tipo de archivo no permitido.'
        
        if (droppedFile.type.startsWith('image/') && !validateImageFile(droppedFile)) {
          errorMessage = 'Solo se permiten imágenes con extensión .jpeg o .png'
        } else {
          errorMessage = `Tipo de archivo no permitido. Solo se permiten: ${getAcceptedFileTypesText()}`
        }
        
        toast.error(errorMessage)
      }
    }
  }

  const getAcceptedFileTypesForInput = () => {
    return acceptedFileTypes.flatMap((type) => fileTypeMappings[type]).join(',')
  }

  const validateFile = (file: File): boolean => {
    const acceptedTypes = getAcceptedFileTypesForInput().split(',')
    return acceptedTypes.includes(file.type)
  }

  const validateImageFile = (file: File): boolean => {
    // Solo validar formato específico para imágenes
    if (file.type.startsWith('image/')) {
      const validImageTypes = ['image/jpeg', 'image/png']
      const validExtensions = ['.jpeg', '.jpg', '.png']
      
      // Verificar MIME type
      const isValidMimeType = validImageTypes.includes(file.type)
      
      // Verificar extensión del archivo
      const fileName = file.name.toLowerCase()
      const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext))
      
      // Para imágenes, solo permitir archivos .jpeg y .png específicamente
      if (file.type === 'image/jpeg') {
        // Si es JPEG, solo permitir archivos .jpeg (no .jpg)
        return fileName.endsWith('.jpeg')
      } else if (file.type === 'image/png') {
        // Si es PNG, permitir archivos .png
        return fileName.endsWith('.png')
      }
      
      return false
    }
    return true // Para otros tipos de archivo, no aplicar restricción
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0]
      
      if (validateFile(selectedFile) && validateImageFile(selectedFile)) {
        clearFilePreview()
        setFile(selectedFile)
        createFilePreview(selectedFile)
      } else {
        let errorMessage = 'Tipo de archivo no permitido.'
        
        if (selectedFile.type.startsWith('image/') && !validateImageFile(selectedFile)) {
          errorMessage = 'Solo se permiten imágenes con extensión .jpeg o .png'
        } else {
          errorMessage = `Tipo de archivo no permitido. Solo se permiten: ${getAcceptedFileTypesText()}`
        }
        
        toast.error(errorMessage)
        // Limpiar el input
        e.target.value = ''
      }
    }
  }

  return (
    <div>
      <div className="space-y-4 p-6">
        <div
          className={`flex flex-col items-center gap-1 rounded-lg border-2 border-dashed p-6 ${
            dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {file ? (
            <div className="w-full space-y-3">
              <div className="text-sm text-gray-700">
                Archivo seleccionado: <strong>{file.name}</strong>
              </div>

              {/* Vista previa del archivo */}
              <div className="rounded-lg p-4">
                {previewUrl ? (
                  // Vista previa para imágenes
                  <div className="flex justify-center">
                    <img
                      src={previewUrl}
                      alt="Vista previa"
                      className="max-h-48 max-w-full rounded border object-contain"
                    />
                  </div>
                ) : (
                  // Vista previa para otros tipos de archivo
                  <div className="flex items-center space-x-3 rounded border bg-white p-3">
                    <div className="flex-shrink-0">
                      {file.type.includes('pdf') && (
                        <svg
                          className="h-8 w-8 text-red-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {file.type.includes('word') && (
                        <svg
                          className="h-8 w-8 text-blue-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {file.type.includes('excel') && (
                        <svg
                          className="h-8 w-8 text-green-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {file.type.includes('text') && (
                        <svg
                          className="h-8 w-8 text-gray-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {!file.type.includes('pdf') &&
                        !file.type.includes('word') &&
                        !file.type.includes('excel') &&
                        !file.type.includes('text') && (
                          <FileIcon className="h-8 w-8 text-gray-500" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {file.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {file.type || 'Tipo desconocido'} •{' '}
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={'flex w-full flex-col items-center'}>
              <FileIcon className="h-12 w-12" />
              <span className="mt-5 text-sm font-medium text-gray-500">
                Arrastra y suelta un archivo o haz clic para buscar
              </span>
              <span className="text-xs text-gray-500">
                Permitidos: {getAcceptedFileTypesText()}
              </span>
            </div>
          )}
          <Input
            id="file"
            type="file"
            className="hidden"
            accept={getAcceptedFileTypesForInput()}
            onChange={handleFileChange}
          />
          <Button
            onClick={() => document.getElementById('file')?.click()}
            variant="outline"
            className={'mt-5'}
            disabled={loading}
          >
            {file ? 'Seleccionar otro archivo' : 'Seleccionar archivo'}
          </Button>
        </div>
      </div>
      <div className={'flex w-full justify-end'}>
        {loading ? (
          <ButtonLoading
            size="default"
            disabled={!file}
            loading={loading}
            name="Subiendo..."
          ></ButtonLoading>
        ) : (
          <Button
            size="default"
            disabled={!file}
            onClick={() => onUpload(file!)}
          >
            Subir archivo
          </Button>
        )}
      </div>
    </div>
  )
}

function FileIcon(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    </svg>
  )
}
